<template>
  <ContentWrap title="商圈参数设置" message="配置平台基础信息、认证定价、积分补给、供需线索与圈子定价（库配置优先于 yaml）">
    <el-form v-loading="loading" :model="form" label-width="180px" class="max-w-720px">
      <el-form-item label="动态发布审核">
        <el-switch v-model="form.feedAuditEnabled" />
        <span class="config-tip">开启后，用户发布的动态需后台审核通过后展示</span>
      </el-form-item>

      <el-divider content-position="left">成为圈主 · 资格费定价</el-divider>
      <el-alert
        class="mb-16px"
        type="info"
        :closable="false"
        show-icon
        title="此处定价后，C 端「成为圈主」按此售价购买；创单金额以服务端读取本配置为准。入圈价由圈主在发布圈子时自行设定。"
      />
      <el-form-item label="资格费售价" prop="ownerFee">
        <el-input-number
          v-model="form.ownerFee"
          :min="0.01"
          :max="999999"
          :precision="2"
          :step="1"
          controls-position="right"
          class="!w-200px"
        />
        <span class="config-tip">元</span>
      </el-form-item>

      <el-divider content-position="left">六度人脉解锁定价</el-divider>
      <el-alert
        class="mb-16px"
        type="info"
        :closable="false"
        show-icon
        title="1 度路径免费；2–6 度按下方单价现金解锁。支付通道（含模拟支付）在「支付管理 → 应用信息 → bosscircle」配置。"
      />
      <el-form-item
        v-for="degree in UNLOCK_DEGREES"
        :key="degree"
        :label="`${degree} 度解锁价`"
        :prop="`unlockPrices.${degree}`"
      >
        <el-input-number
          v-model="form.unlockPrices[degree]"
          :min="0.01"
          :max="99999"
          :precision="2"
          :step="1"
          controls-position="right"
          class="!w-200px"
        />
        <span class="config-tip">元（保存后按分写入配置）</span>
      </el-form-item>

      <el-divider content-position="left">Boss 圈积分 · 任务中心</el-divider>
      <el-alert
        class="mb-16px"
        type="warning"
        :closable="false"
        show-icon
        title="与商城/会员积分相互独立。此处改奖励后立即生效（读库优先），无需改 yaml。"
      />
      <el-form-item label="任务中心开关">
        <el-switch v-model="form.pointTasksEnabled" />
      </el-form-item>
      <el-form-item label="每日签到奖励">
        <el-input-number v-model="form.signReward" :min="0" :max="99999" controls-position="right" class="!w-200px" />
        <span class="config-tip">Boss 圈积分</span>
      </el-form-item>
      <el-form-item label="完善资料奖励">
        <el-input-number v-model="form.profileReward" :min="0" :max="99999" controls-position="right" class="!w-200px" />
        <span class="config-tip">Boss 圈积分</span>
      </el-form-item>
      <el-form-item label="完成认证奖励">
        <el-input-number v-model="form.certReward" :min="0" :max="99999" controls-position="right" class="!w-200px" />
        <span class="config-tip">Boss 圈积分</span>
      </el-form-item>
      <el-form-item label="首发供需奖励">
        <el-input-number v-model="form.bizoppReward" :min="0" :max="99999" controls-position="right" class="!w-200px" />
        <span class="config-tip">Boss 圈积分</span>
      </el-form-item>

      <el-divider content-position="left">Boss 圈积分 · 购分套餐</el-divider>
      <el-form-item label="购分开关">
        <el-switch v-model="form.pointBuyEnabled" />
      </el-form-item>
      <el-form-item label="购分套餐">
        <div class="pkg-box">
          <div v-for="(pkg, idx) in form.buyPackages" :key="idx" class="pkg-row">
            <el-input v-model="pkg.code" placeholder="编码" class="!w-100px" />
            <el-input v-model="pkg.name" placeholder="名称" class="!w-120px" />
            <el-input-number v-model="pkg.points" :min="1" :max="999999" controls-position="right" class="!w-130px" />
            <span class="config-tip">分</span>
            <el-input-number
              v-model="pkg.priceYuan"
              :min="0.01"
              :max="99999"
              :precision="2"
              controls-position="right"
              class="!w-140px"
            />
            <span class="config-tip">元</span>
            <el-button link type="danger" @click="form.buyPackages.splice(idx, 1)">删除</el-button>
          </div>
          <el-button type="primary" link @click="addPackage">+ 添加套餐</el-button>
        </div>
      </el-form-item>

      <el-divider content-position="left">个人 / 企业认证定价</el-divider>
      <el-alert
        class="mb-16px"
        type="info"
        :closable="false"
        show-icon
        title="个人与企业各自独立。免费时价格忽略；付费走统一订单 type=28（bosscircle 支付应用）。驳回重提默认不再收费。"
      />
      <el-form-item label="个人认证开放">
        <el-switch v-model="form.certPersonalEnabled" />
      </el-form-item>
      <el-form-item label="个人计费方式">
        <el-radio-group v-model="form.certPersonalChargeMode">
          <el-radio value="FREE">免费</el-radio>
          <el-radio value="PAID">付费</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item v-if="form.certPersonalChargeMode === 'PAID'" label="个人认证售价">
        <el-input-number
          v-model="form.certPersonalPriceYuan"
          :min="0.01"
          :max="99999"
          :precision="2"
          controls-position="right"
          class="!w-200px"
        />
        <span class="config-tip">元</span>
      </el-form-item>
      <el-form-item label="个人说明文案">
        <el-input v-model="form.certPersonalTip" maxlength="200" show-word-limit class="!w-420px" />
      </el-form-item>
      <el-form-item label="企业认证开放">
        <el-switch v-model="form.certEnterpriseEnabled" />
      </el-form-item>
      <el-form-item label="企业计费方式">
        <el-radio-group v-model="form.certEnterpriseChargeMode">
          <el-radio value="FREE">免费</el-radio>
          <el-radio value="PAID">付费</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item v-if="form.certEnterpriseChargeMode === 'PAID'" label="企业认证售价">
        <el-input-number
          v-model="form.certEnterprisePriceYuan"
          :min="0.01"
          :max="99999"
          :precision="2"
          controls-position="right"
          class="!w-200px"
        />
        <span class="config-tip">元</span>
      </el-form-item>
      <el-form-item label="企业说明文案">
        <el-input v-model="form.certEnterpriseTip" maxlength="200" show-word-limit class="!w-420px" />
      </el-form-item>

      <el-divider content-position="left">供需线索</el-divider>
      <el-alert
        class="mb-16px"
        type="info"
        :closable="false"
        show-icon
        title="默认查看积分：发布时选「收费」且未填积分时的默认值。上限限制发起人可设范围。联系方式 AES 密钥不在此配置（运维/yaml）。"
      />
      <el-form-item label="默认查看积分">
        <el-input-number v-model="form.defaultViewPoints" :min="0" :max="99999" controls-position="right" class="!w-200px" />
      </el-form-item>
      <el-form-item label="查看积分上限">
        <el-input-number v-model="form.maxViewPoints" :min="0" :max="99999" controls-position="right" class="!w-200px" />
      </el-form-item>

      <el-form-item>
        <el-button
          v-hasPermi="['bosscircle:config:update']"
          type="primary"
          :loading="saving"
          @click="save"
        >
          保存配置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>
</template>

<script setup lang="ts">
import * as Api from '@/api/business/admin'

defineOptions({ name: 'BossCircleConfig' })

const UNLOCK_DEGREES = [2, 3, 4, 5, 6] as const
const DEFAULT_UNLOCK_YUAN: Record<number, number> = {
  2: 1,
  3: 2,
  4: 3,
  5: 4,
  6: 5
}
const DEFAULT_OWNER_FEE = 99
const DEFAULT_PACKAGES = [
  { code: 'p10', name: '10积分', points: 10, priceYuan: 1 },
  { code: 'p50', name: '50积分', points: 50, priceYuan: 5 },
  { code: 'p200', name: '200积分', points: 200, priceYuan: 18 }
]

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const form = reactive({
  siteName: 'BossCircle',
  feedAuditEnabled: false,
  activitySignupEnabled: true,
  ownerFee: DEFAULT_OWNER_FEE,
  unlockPrices: { 2: 1, 3: 2, 4: 3, 5: 4, 6: 5 } as Record<number, number>,
  pointTasksEnabled: true,
  signReward: 10,
  profileReward: 30,
  certReward: 50,
  bizoppReward: 20,
  pointBuyEnabled: true,
  buyPackages: DEFAULT_PACKAGES.map((p) => ({ ...p })),
  certPersonalEnabled: true,
  certPersonalChargeMode: 'FREE' as 'FREE' | 'PAID',
  certPersonalPriceYuan: 0,
  certPersonalTip: '个人实名认证，审核通过后可展示实名徽标',
  certEnterpriseEnabled: true,
  certEnterpriseChargeMode: 'FREE' as 'FREE' | 'PAID',
  certEnterprisePriceYuan: 0,
  certEnterpriseTip: '企业认证，审核通过后可展示企认徽标',
  defaultViewPoints: 10,
  maxViewPoints: 999
})

const toBoolean = (value: unknown, fallback: boolean) => {
  if (value === undefined || value === null || value === '') return fallback
  return String(value).toLowerCase() === 'true' || String(value) === '1'
}

const fenToYuan = (fen: unknown, fallback: number) => {
  const n = Number(fen)
  if (!Number.isFinite(n) || n < 0) return fallback
  if (n === 0) return 0
  return Math.round(n) / 100
}

const yuanToFen = (yuan: number) => Math.max(0, Math.round(yuan * 100))

const yuanToFenPositive = (yuan: number) => Math.max(1, Math.round(yuan * 100))

const parseOwnerFeeYuan = (raw: unknown, fallback: number) => {
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return fallback
  return Math.round(n * 100) / 100
}

const parseIntValue = (raw: unknown, fallback: number) => {
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 0) return fallback
  return Math.floor(n)
}

const parsePackages = (raw: unknown) => {
  if (!raw) return DEFAULT_PACKAGES.map((p) => ({ ...p }))
  try {
    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!Array.isArray(arr) || !arr.length) return DEFAULT_PACKAGES.map((p) => ({ ...p }))
    return arr.map((item: any) => ({
      code: String(item.code || ''),
      name: String(item.name || `${item.points || ''}积分`),
      points: Number(item.points) || 0,
      priceYuan: Number(item.priceYuan ?? item['price-yuan']) || 0
    }))
  } catch {
    return DEFAULT_PACKAGES.map((p) => ({ ...p }))
  }
}

const addPackage = () => {
  form.buyPackages.push({ code: '', name: '', points: 10, priceYuan: 1 })
}

const load = async () => {
  loading.value = true
  try {
    const list: any[] = (await Api.getConfigs()) || []
    const values = Object.fromEntries(list.map((item) => [item.configKey, item.configValue]))
    form.siteName = values.site_name || 'BossCircle'
    form.feedAuditEnabled = toBoolean(values.feed_audit, false)
    form.activitySignupEnabled = toBoolean(values.activity_signup, true)
    form.ownerFee = parseOwnerFeeYuan(values.CIRCLE_OWNER_FEE, DEFAULT_OWNER_FEE)
    UNLOCK_DEGREES.forEach((degree) => {
      form.unlockPrices[degree] = fenToYuan(
        values[`CONNECTION_UNLOCK_PRICE_${degree}`],
        DEFAULT_UNLOCK_YUAN[degree]
      )
    })
    form.pointTasksEnabled = toBoolean(values.POINT_TASKS_ENABLED, true)
    form.signReward = parseIntValue(values.POINT_SIGN_REWARD, 10)
    form.profileReward = parseIntValue(values.POINT_TASK_PROFILE_REWARD, 30)
    form.certReward = parseIntValue(values.POINT_TASK_CERT_REWARD, 50)
    form.bizoppReward = parseIntValue(values.POINT_TASK_BIZOPP_REWARD, 20)
    form.pointBuyEnabled = toBoolean(values.POINT_BUY_ENABLED, true)
    form.buyPackages = parsePackages(values.POINT_BUY_PACKAGES)
    form.certPersonalEnabled = toBoolean(values.CERT_PERSONAL_ENABLED, true)
    form.certPersonalChargeMode =
      String(values.CERT_PERSONAL_CHARGE_MODE || 'FREE').toUpperCase() === 'PAID' ? 'PAID' : 'FREE'
    form.certPersonalPriceYuan = fenToYuan(values.CERT_PERSONAL_PRICE_FEN, 0)
    form.certPersonalTip = values.CERT_PERSONAL_TIP || form.certPersonalTip
    form.certEnterpriseEnabled = toBoolean(values.CERT_ENTERPRISE_ENABLED, true)
    form.certEnterpriseChargeMode =
      String(values.CERT_ENTERPRISE_CHARGE_MODE || 'FREE').toUpperCase() === 'PAID' ? 'PAID' : 'FREE'
    form.certEnterprisePriceYuan = fenToYuan(values.CERT_ENTERPRISE_PRICE_FEN, 0)
    form.certEnterpriseTip = values.CERT_ENTERPRISE_TIP || form.certEnterpriseTip
    form.defaultViewPoints = parseIntValue(values.BIZOPP_DEFAULT_VIEW_POINTS, 10)
    form.maxViewPoints = parseIntValue(values.BIZOPP_MAX_VIEW_POINTS, 999)
  } finally {
    loading.value = false
  }
}

const save = async () => {
  if (!form.siteName.trim()) {
    message.warning('请输入平台名称')
    return
  }
  if (form.ownerFee === undefined || form.ownerFee === null || form.ownerFee <= 0) {
    message.warning('圈主资格费售价须大于 0 元')
    return
  }
  for (const degree of UNLOCK_DEGREES) {
    const price = form.unlockPrices[degree]
    if (price === undefined || price === null || price <= 0) {
      message.warning(`${degree} 度解锁价须大于 0 元`)
      return
    }
  }
  if (form.maxViewPoints < form.defaultViewPoints) {
    message.warning('查看积分上限不能小于默认查看积分')
    return
  }
  if (form.pointBuyEnabled) {
    if (!form.buyPackages.length) {
      message.warning('请至少配置一个购分套餐')
      return
    }
    for (const pkg of form.buyPackages) {
      if (!pkg.code?.trim() || !pkg.points || pkg.points <= 0 || !pkg.priceYuan || pkg.priceYuan <= 0) {
        message.warning('购分套餐编码/积分/价格不完整')
        return
      }
    }
  }
  if (form.certPersonalChargeMode === 'PAID' && (!form.certPersonalPriceYuan || form.certPersonalPriceYuan <= 0)) {
    message.warning('个人认证付费时售价须大于 0 元')
    return
  }
  if (form.certEnterpriseChargeMode === 'PAID' && (!form.certEnterprisePriceYuan || form.certEnterprisePriceYuan <= 0)) {
    message.warning('企业认证付费时售价须大于 0 元')
    return
  }
  saving.value = true
  try {
    const unlockConfigs = UNLOCK_DEGREES.map((degree) => ({
      configKey: `CONNECTION_UNLOCK_PRICE_${degree}`,
      configValue: String(yuanToFenPositive(form.unlockPrices[degree])),
      remark: `六度${degree}度解锁价（分）`
    }))
    const packagesJson = JSON.stringify(
      form.buyPackages.map((p) => ({
        code: p.code.trim(),
        name: (p.name || `${p.points}积分`).trim(),
        points: p.points,
        priceYuan: p.priceYuan
      }))
    )
    await Api.saveConfigs([
      { configKey: 'site_name', configValue: form.siteName.trim(), remark: '平台名称' },
      { configKey: 'feed_audit', configValue: String(form.feedAuditEnabled), remark: '动态发布审核' },
      {
        configKey: 'activity_signup',
        configValue: String(form.activitySignupEnabled),
        remark: '活动报名开关'
      },
      {
        configKey: 'CIRCLE_OWNER_FEE',
        configValue: String(form.ownerFee),
        remark: '圈主资格费售价（元）；C端按价购买'
      },
      ...unlockConfigs,
      {
        configKey: 'POINT_TASKS_ENABLED',
        configValue: String(form.pointTasksEnabled),
        remark: 'Boss圈任务中心开关'
      },
      {
        configKey: 'POINT_SIGN_REWARD',
        configValue: String(form.signReward),
        remark: '每日签到 Boss圈积分奖励'
      },
      {
        configKey: 'POINT_TASK_PROFILE_REWARD',
        configValue: String(form.profileReward),
        remark: '完善资料任务奖励积分'
      },
      {
        configKey: 'POINT_TASK_CERT_REWARD',
        configValue: String(form.certReward),
        remark: '认证通过任务奖励积分'
      },
      {
        configKey: 'POINT_TASK_BIZOPP_REWARD',
        configValue: String(form.bizoppReward),
        remark: '首发供需任务奖励积分'
      },
      {
        configKey: 'POINT_BUY_ENABLED',
        configValue: String(form.pointBuyEnabled),
        remark: 'Boss圈购分开关'
      },
      {
        configKey: 'POINT_BUY_PACKAGES',
        configValue: packagesJson,
        remark: 'Boss圈购分套餐JSON'
      },
      {
        configKey: 'CERT_PERSONAL_ENABLED',
        configValue: String(form.certPersonalEnabled),
        remark: '个人认证是否开放提交'
      },
      {
        configKey: 'CERT_PERSONAL_CHARGE_MODE',
        configValue: form.certPersonalChargeMode,
        remark: '个人认证计费：FREE|PAID'
      },
      {
        configKey: 'CERT_PERSONAL_PRICE_FEN',
        configValue: String(
          form.certPersonalChargeMode === 'PAID'
            ? yuanToFenPositive(form.certPersonalPriceYuan)
            : yuanToFen(form.certPersonalPriceYuan || 0)
        ),
        remark: '个人认证价格（分）'
      },
      {
        configKey: 'CERT_PERSONAL_TIP',
        configValue: form.certPersonalTip.trim(),
        remark: '个人认证 C 端说明'
      },
      {
        configKey: 'CERT_ENTERPRISE_ENABLED',
        configValue: String(form.certEnterpriseEnabled),
        remark: '企业认证是否开放提交'
      },
      {
        configKey: 'CERT_ENTERPRISE_CHARGE_MODE',
        configValue: form.certEnterpriseChargeMode,
        remark: '企业认证计费：FREE|PAID'
      },
      {
        configKey: 'CERT_ENTERPRISE_PRICE_FEN',
        configValue: String(
          form.certEnterpriseChargeMode === 'PAID'
            ? yuanToFenPositive(form.certEnterprisePriceYuan)
            : yuanToFen(form.certEnterprisePriceYuan || 0)
        ),
        remark: '企业认证价格（分）'
      },
      {
        configKey: 'CERT_ENTERPRISE_TIP',
        configValue: form.certEnterpriseTip.trim(),
        remark: '企业认证 C 端说明'
      },
      {
        configKey: 'BIZOPP_DEFAULT_VIEW_POINTS',
        configValue: String(form.defaultViewPoints),
        remark: '供需收费帖默认查看积分'
      },
      {
        configKey: 'BIZOPP_MAX_VIEW_POINTS',
        configValue: String(form.maxViewPoints),
        remark: '供需查看积分上限'
      }
    ])
    message.success('保存成功')
    await load()
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.config-tip {
  margin-left: 12px;
  color: var(--el-text-color-secondary);
  font-size: 12px;
}
.pkg-box {
  width: 100%;
}
.pkg-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
</style>
